import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Info, PlayCircle } from "lucide-react";
import api from "../lib/api";
import LiveBadge from "./LiveBadge";

export default function HeroSlider() {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/hero-slides").then((r) => setSlides(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    clearInterval(timer.current);
    timer.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(timer.current);
  }, [slides.length]);

  if (!slides.length) return null;
  const s = slides[idx] || slides[0];
  const isLive = (s.badge || "").toUpperCase() === "LIVE";
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);

  return (
    <section data-testid="hero-slider" className="relative w-full">
      {/* Mobile: contained card with rounded corners (unchanged) */}
      <div className="md:hidden mx-auto max-w-7xl px-3 sm:px-6 py-4">
        <HeroFrame s={s} idx={idx} isLive={isLive} slides={slides} prev={prev} next={next} setIdx={setIdx} rounded />
      </div>
      {/* Desktop: full-width edge-to-edge banner across the page */}
      <div className="hidden md:block">
        <HeroFrame s={s} idx={idx} isLive={isLive} slides={slides} prev={prev} next={next} setIdx={setIdx} rounded={false} fullbleed />
      </div>
    </section>
  );
}

function HeroFrame({ s, idx, isLive, slides, prev, next, setIdx, rounded, fullbleed }) {
  const radius = rounded ? "rounded-2xl md:rounded-3xl" : "";
  const aspect = fullbleed ? "h-[42vw] max-h-[640px] min-h-[420px]" : "aspect-[16/9] sm:aspect-[21/9]";
  return (
    <div className={`relative ${aspect} ${radius} overflow-hidden tv-glass`}>
        {s.trailer_url ? (
          <video
            key={s.id}
            src={s.trailer_url}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <img
            key={s.id}
            src={s.image_url}
            alt={s.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

        <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-10 md:p-14 max-w-3xl">
          <div className="flex items-center gap-2 mb-2">
            {isLive && <LiveBadge />}
            {!isLive && s.badge && (
              <span className="bg-tv-brand text-black text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded">
                {s.badge}
              </span>
            )}
            {s.subtitle && !isLive && (
              <span className="text-[11px] uppercase tracking-[0.25em] text-tv-accent font-bold">{s.subtitle}</span>
            )}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-white tracking-tighter leading-[1.02]" data-testid={`hero-slide-title-${idx}`}>
            {s.title}
          </h2>
          {s.description && (
            <p className="mt-3 text-white/75 text-sm sm:text-base max-w-xl line-clamp-3">{s.description}</p>
          )}
          <div className="mt-5 flex flex-wrap gap-3">
            {s.watch_url && (
              <Link
                to={s.watch_url}
                data-testid="hero-slide-watch-btn"
                className={`${isLive ? "bg-tv-live hover:bg-red-600 text-white" : "bg-white hover:bg-white/90 text-black"} font-bold px-6 py-3 rounded-full flex items-center gap-2`}
              >
                <PlayCircle className="w-5 h-5" /> {isLive ? "Watch Live" : "Watch"}
              </Link>
            )}
            {s.info_url && (
              <Link
                to={s.info_url}
                data-testid="hero-slide-info-btn"
                className="tv-glass hover:bg-white/10 text-white font-semibold px-6 py-3 rounded-full flex items-center gap-2 border border-white/20"
              >
                <Info className="w-5 h-5" /> More Info
              </Link>
            )}
          </div>
        </div>

        {slides.length > 1 && (
          <>
            <button
              data-testid="hero-slider-prev"
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white hover:bg-white/10"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="hero-slider-next"
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full tv-glass flex items-center justify-center text-white hover:bg-white/10"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-4 right-5 flex gap-1.5 z-10">
              {slides.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-slider-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-tv-accent" : "w-3 bg-white/30 hover:bg-white/50"}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
    </div>
  );
}
